import { useEffect, useState } from 'react';
import { Box, Card, CardContent, CardHeader, Grid, Typography, Divider, Input } from '@mui/material';
import Spinner from '../../Spinner';
import ErrorSummary from '../../common/ErrorSummary';
import { useInvoice, InvoiceProvider } from '../../common/InvoiceProvider';
import InvoicingForm from 'components/Invoicing/InvoicingForm';
import { useWorkflow, WorkflowProvider } from 'components/Workflow/WorkflowContext';
import PdfViewer from 'components/common/PdfRenderer';
import WorkflowLogEntries from 'components/Workflow/WorkflowLogEntries';
import { hasFunctionality } from 'components/authorization/PermissionUtils';
import { useParams } from 'react-router';

const InvoicingDetailContent = () => {
  const { input, isLoading, errors } = useInvoice();
  const { workflow, loading: workflowLoading } = useWorkflow();
  const { id } = useParams();
  const [disabledForm, setDisabledForm] = useState<boolean>(false);

  useEffect(() => {
    if (workflow && !workflowLoading) {
      setDisabledForm(!workflow.states.find((state) => state.id === input.idWorkflowState)?.editable);
    }
  }, [workflow, workflowLoading, input.idWorkflowState]);

  return (
    <Box sx={{ mt: 4, px: 2 }}>
      <Card variant="outlined">
        {isLoading && <Spinner />}
        <CardHeader
          title={
            <Typography color="primary" variant="h2">
              Invoicing Detail
            </Typography>
          }
        />
        <CardContent>
          <Grid container spacing={2}>
            <Grid sx={{ xs: 12 }}>
              <ErrorSummary errors={errors} showEverything={true} />
            </Grid>

            <Grid sx={{ xs: 12 }}>
              <Typography
                sx={{ mt: errors?.idFrom && errors?.idFrom !== 'missing confidence' ? -3 : 0 }}
                color={errors?.idFrom ? 'error' : 'primary'}
                variant="body2"
              >
                {errors?.idFrom && errors?.idFrom !== 'missing confidence' && errors?.idFrom}
              </Typography>
              <Input
                id="idFrom"
                name="idFrom"
                size="small"
                value={input?.personaFrom?.name || ''}
                disabled
                disableUnderline
                sx={{ width: '100%', p: 1, mt: 1, border: '1px solid #ccc', borderRadius: 1 }}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {input ? (
            <Grid container spacing={2} alignItems="flex-start" sx={{ width: '100%' }}>
              {input?.attachments && (
                <Grid size={{ xs: 12, md: 5 }}>
                  <Box mb={3}>
                    <PdfViewer
                      file={input.attachments?.[0]}
                      dataURI={input.attachments?.[0]?.dataUri}
                    />
                  </Box>
                </Grid>
              )}
              <Grid size={{ xs: 12, md: input?.attachments ? 7 : 12 }} sx={{ minWidth: 0 }}>
                <InvoicingForm disabled={disabledForm} />
              </Grid>
            </Grid>
          ) : (
            <Spinner />
          )}

          {(hasFunctionality('ui.workflowlog.invoicing.sender') || hasFunctionality('ui.workflowlog.invoicing.receiver')) && <WorkflowLogEntries idModule={3} idEntity={Number(id)} />}
        </CardContent>
      </Card>
    </Box>
  );
};

const InvoicingDetail = () => {
  return (
    <InvoiceProvider review={false} view={false} edit={true}>
      <WorkflowProvider moduleId={3}>
        <InvoicingDetailContent />
      </WorkflowProvider>
    </InvoiceProvider>
  );
};

export default InvoicingDetail;
